import { useDispatch, useSelector } from 'react-redux'
import {
  selectCustomerSubscriptionData,
  selectEnergyCharacteristicsSubscriptionData,
  selectEnergyContractSubscriptionData,
  selectGasMeterSubscriptionData,
} from '@/containers/SubscriptionFormPage/SubscriptionFormSelectors'

import {
  selectCommonFormConfigurationData,
  selectOfferModuleConfigurationData,
} from '@/containers/SubscriptionFormPage/SubscriptionFormConfigurationSelectors'
import { useTranslation } from 'react-i18next'
import { clubSelectraModuleActions } from '@/containers/SubscriptionFormPageNew/Components/ClubSelectra/ClubSelectraSagas'
import {
  selectClubSelectraState,
  selectConfig,
  selectPersonalClientSubscriptionData,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSelectors'
import { useSnackbar } from 'baseui/snackbar'
import useBusinessType from '../../../../shared/hooks/useBusinessType'
import {
  BUSINESS_VERTICAL_ENERGY,
  BUSINESS_VERTICAL_INSURANCE,
  BUSINESS_VERTICAL_TELECOM,
} from '../../SubscriptionForm/Common/constants'
import { selectActiveTabType } from '../../SubscriptionForm/SubscriptionFormSelectors'
import {
  selectTelecomCharacteristicsSubscriptionData,
  selectTelecomContractSubscriptionData,
} from '../../../SubscriptionFormPage/SubscriptionFormSelectors'
import { selectOfferConfiguration } from '../../../SubscriptionFormPage/SubscriptionFormConfigurationSelectors'
import { selectAuthData } from '../../../Login/AuthSelectors'

// to-do-v2: selectGasMeterSubscriptionData no more used in V2 and all in path SubscriptionFormPage/*
const useClubSelectra = ({ version, module }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { enqueue } = useSnackbar()
  const { typeTranslated: applicationType } = useBusinessType({
    loaderRoutesEnergy: {},
    loaderRoutesInsurance: {},
    loaderRoutes: {},
    modulesStatuses: {},
    modulesStatusesEnergy: {},
    modulesStatusesInsurance: {},
  })
  const { country } = useSelector(selectAuthData)
  const contractApplicationType = version === 'legacy' ? applicationType : useSelector(selectActiveTabType)
  const { energyType, prestationType: energyPrestationType } = useSelector(selectEnergyCharacteristicsSubscriptionData)
  const { prestationType: telecomPrestationType, productType } = useSelector(
    selectTelecomCharacteristicsSubscriptionData,
  )

  const contractType = (contractApplicationType || '').toLowerCase()
  const isEnergyBusinessVertical = contractType === BUSINESS_VERTICAL_ENERGY

  const { offerId, providerExternalId } = isEnergyBusinessVertical
    ? useSelector(selectEnergyContractSubscriptionData)
    : useSelector(selectTelecomContractSubscriptionData)

  const { providerExternalIdGas } = useSelector(selectGasMeterSubscriptionData)
  const { offers } = useSelector(selectOfferModuleConfigurationData)
  const { name: offerName, organization } = useSelector(selectOfferConfiguration)
  const { isClubSelectraRegistered } = useSelector(selectCustomerSubscriptionData)
  const clubSelectra = useSelector(selectClubSelectraState)
  const isLoading = clubSelectra.loading
  const success = clubSelectra.success
  const message = clubSelectra.message || ''
  const error = clubSelectra.error || ''

  const sourceClientIdSelector = version === 'legacy' ? selectCommonFormConfigurationData : selectConfig
  const sourceClientIdData = useSelector(sourceClientIdSelector) || {}
  const clientId = version === 'legacy' ? sourceClientIdData?.id : sourceClientIdData?.contactId
  const sourceClientSelector =
    version === 'legacy' ? selectCustomerSubscriptionData : selectPersonalClientSubscriptionData

  const client = useSelector(sourceClientSelector) || {}
  const { zohoId } = client || ''
  const contactFirstName = version === 'legacy' ? (client?.name ?? '') : (client?.firstName ?? '')
  const contactLastName = version === 'legacy' ? (client?.surname ?? '') : (client?.lastName ?? '')
  const contactEmail = client?.email ?? ''

  const createClubSelectra = () => {
    if (isLoading) return

    dispatch({
      type: clubSelectraModuleActions.CREATE_CLUB_SELECTRA,
      enqueue,
      payload: buildPayload(),
      version,
    })
  }

  const isEmptyValue = value => !value || String(value).trim() === ''

  const isContactModule = module === 'contact'

  const baseMandatoryEntries = [
    [t('subscriptionForm:customer_module.firstname.label'), contactFirstName],
    [t('subscriptionForm:customer_module.lastname.label'), contactLastName],
    [t('subscriptionForm:customer_module.email.label'), contactEmail],
    [t('subscriptionForm:zoho_module.module_title'), zohoId],
    ['Subscription id', clientId],
  ]

  const mandatoryEnergyFields = [[t('subscriptionForm:characteristics_module.energy_type.label'), energyType]]

  const mandatoryTelecomFields = [[t('subscriptionForm:characteristics_module.product_type.label'), productType]]

  const extraMandatoryFields = isEnergyBusinessVertical ? mandatoryEnergyFields : mandatoryTelecomFields
  const mandatoryEntries = [
    ...baseMandatoryEntries,
    ...(!isContactModule
      ? [...extraMandatoryFields, [t('subscriptionForm:characteristics_module.contract_type.label'), offerName]]
      : []),
  ]
  const currentMissingLabels = mandatoryEntries.filter(([, value]) => isEmptyValue(value)).map(([label]) => label)
  const labelsToShow = !isClubSelectraRegistered ? currentMissingLabels : []
  const prestationType = isEnergyBusinessVertical ? energyPrestationType : telecomPrestationType

  const buildPayload = () => {
    const payload = {
      contact_zoho_id: zohoId,
      contact_first_name: contactFirstName,
      contact_last_name: contactLastName,
      country,
      contact_email: contactEmail,
      client_id: clientId,
      version,
      offer_name: offerName ?? '',
      business_vertical: contractType,
      prestation_type: prestationType ?? '',
      provider_zoho_prefix: organization?.zoho_prefix ?? '',
    }

    if (contractType === BUSINESS_VERTICAL_TELECOM && productType) {
      payload.product_type = productType
    }

    if (isEnergyBusinessVertical && energyType) {
      payload.energy_type = energyType
    }

    if ((energyType === 'elec' || energyType === 'dual') && providerExternalId) {
      payload.provider_external_id_elec = providerExternalId
    }

    if ((energyType === 'gas' || energyType === 'dual') && providerExternalIdGas) {
      payload.provider_external_id_gas = providerExternalIdGas
    }

    return payload
  }

  const disabled =
    contractType === BUSINESS_VERTICAL_INSURANCE ||
    isLoading ||
    isClubSelectraRegistered === true ||
    currentMissingLabels.length > 0 ||
    success === true

  return {
    t,
    isLoading,
    success,
    message,
    error,
    isClubSelectraRegistered,
    energyType,
    offerId,
    offers,
    zohoId,
    clientId,
    contactFirstName,
    contactLastName,
    contactEmail,
    offerName,
    labelsToShow:
      contractType === BUSINESS_VERTICAL_INSURANCE
        ? [t('subscriptionForm:header.modal_save_send.club_selectra_no_vertical_integrated')]
        : labelsToShow,
    currentMissingLabels,
    disabled,
    createClubSelectra,
    isContactModule,
  }
}

export default useClubSelectra
